import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ProcessIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchCategories } from '../store/categoriesSlice';
import api from '../services/api';

interface RecurringTransaction {
  id: number;
  description: string;
  amount: number;
  currency: string;
  category_id: number;
  is_active: boolean;
  last_posted_month?: number;
  last_posted_year?: number;
  category?: {
    id: number;
    name: string;
    color: string;
    icon: string;
  };
}

interface CreateRecurringTransactionData {
  description: string;
  amount: number;
  currency: string;
  category_id: number;
}

interface UpdateRecurringTransactionData {
  description?: string;
  amount?: number;
  currency?: string;
  category_id?: number;
  is_active?: boolean;
}

const RecurringTransactions: React.FC = () => {
  const dispatch = useAppDispatch();
  const { categories } = useAppSelector((state) => state.categories);
  
  const [recurringTransactions, setRecurringTransactions] = useState<RecurringTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<RecurringTransaction | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [formData, setFormData] = useState<CreateRecurringTransactionData>({
    description: '',
    amount: 0,
    currency: 'CHF',
    category_id: 0,
  });

  useEffect(() => {
    dispatch(fetchCategories());
    fetchRecurringTransactions();
  }, [dispatch]);

  const fetchRecurringTransactions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/recurring-transactions/');
      setRecurringTransactions(response.data);
    } catch (error) {
      console.error('Error fetching recurring transactions:', error);
      setSnackbar({ open: true, message: 'Error fetching recurring transactions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingTransaction) {
        const updateData: UpdateRecurringTransactionData = {};
        if (formData.description !== editingTransaction.description) updateData.description = formData.description;
        if (formData.amount !== editingTransaction.amount) updateData.amount = formData.amount;
        if (formData.currency !== editingTransaction.currency) updateData.currency = formData.currency;
        if (formData.category_id !== editingTransaction.category_id) updateData.category_id = formData.category_id;
        
        await api.put(`/recurring-transactions/${editingTransaction.id}`, updateData);
        setSnackbar({ open: true, message: 'Recurring transaction updated successfully', severity: 'success' });
      } else {
        await api.post('/recurring-transactions/', formData);
        setSnackbar({ open: true, message: 'Recurring transaction created successfully', severity: 'success' });
      }
      
      setDialogOpen(false);
      setEditingTransaction(null);
      fetchRecurringTransactions();
      resetForm();
    } catch (error) {
      console.error('Error saving recurring transaction:', error);
      setSnackbar({ open: true, message: 'Error saving recurring transaction', severity: 'error' });
    }
  };

  const handleEdit = (transaction: RecurringTransaction) => {
    setEditingTransaction(transaction);
    setFormData({
      description: transaction.description,
      amount: transaction.amount,
      currency: transaction.currency,
      category_id: transaction.category_id,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this recurring transaction?')) {
      try {
        await api.delete(`/recurring-transactions/${id}`);
        setSnackbar({ open: true, message: 'Recurring transaction deleted successfully', severity: 'success' });
        fetchRecurringTransactions();
      } catch (error) {
        console.error('Error deleting recurring transaction:', error);
        setSnackbar({ open: true, message: 'Error deleting recurring transaction', severity: 'error' });
      }
    }
  };

  const handleToggleActive = async (transaction: RecurringTransaction) => {
    try {
      await api.put(`/recurring-transactions/${transaction.id}`, { is_active: !transaction.is_active });
      setSnackbar({ open: true, message: `Recurring transaction ${!transaction.is_active ? 'activated' : 'deactivated'}`, severity: 'success' });
      fetchRecurringTransactions();
    } catch (error) {
      console.error('Error updating recurring transaction:', error);
      setSnackbar({ open: true, message: 'Error updating recurring transaction', severity: 'error' });
    }
  };

  const handleProcessAll = async () => {
    try {
      const response = await api.post('/recurring-transactions/process-monthly');
      setSnackbar({ 
        open: true, 
        message: `Processed ${response.data.processed_count} recurring transactions`, 
        severity: 'success' 
      });
      fetchRecurringTransactions();
    } catch (error) {
      console.error('Error processing recurring transactions:', error);
      setSnackbar({ open: true, message: 'Error processing recurring transactions', severity: 'error' });
    }
  };

  const handleProcessSingle = async (id: number) => {
    try {
      await api.post(`/recurring-transactions/manual-process/${id}`);
      setSnackbar({ open: true, message: 'Recurring transaction processed successfully', severity: 'success' });
      fetchRecurringTransactions();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Error processing recurring transaction';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: 0,
      currency: 'CHF',
      category_id: 0,
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingTransaction(null);
    setDialogOpen(true);
  };

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Recurring Transactions
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            About Recurring Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Recurring transactions are automatically posted on the 15th of each month. 
            You can manage your regular expenses and income here, and they'll be added to your transactions automatically.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Add Recurring Transaction
            </Button>
            <Button
              variant="outlined"
              startIcon={<ProcessIcon />}
              onClick={handleProcessAll}
              color="primary"
            >
              Process All for This Month
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Posted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recurringTransactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon color="action" />
                    {transaction.description}
                  </Box>
                </TableCell>
                <TableCell>
                  {transaction.amount} {transaction.currency}
                </TableCell>
                <TableCell>
                  {transaction.category && (
                    <Chip
                      label={transaction.category.name}
                      sx={{ backgroundColor: transaction.category.color, color: 'white' }}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={transaction.is_active}
                        onChange={() => handleToggleActive(transaction)}
                        size="small"
                      />
                    }
                    label={transaction.is_active ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  {transaction.last_posted_month && transaction.last_posted_year ? (
                    <Box>
                      <Typography variant="body2">
                        {transaction.last_posted_month}/{transaction.last_posted_year}
                      </Typography>
                      {transaction.last_posted_month === currentMonth && 
                       transaction.last_posted_year === currentYear && (
                        <Chip label="This Month" color="success" size="small" />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Never
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(transaction)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleProcessSingle(transaction.id)} 
                    size="small"
                    disabled={!transaction.is_active || 
                             (transaction.last_posted_month === currentMonth && 
                              transaction.last_posted_year === currentYear)}
                  >
                    <ProcessIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(transaction.id)} 
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTransaction ? 'Edit Recurring Transaction' : 'Create Recurring Transaction'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                label="Currency"
              >
                <MenuItem value="CHF">CHF</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: Number(e.target.value) })}
                label="Category"
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.description || !formData.amount || !formData.category_id}
          >
            {editingTransaction ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RecurringTransactions;